import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Plus } from "lucide-react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  loadBulkCatalogs,
  loadVatPercent,
  revokePhotoUrls,
  submitBulkListings,
  validateListingsOnServer,
  validatePhotoFile,
} from "./bulkCreate/apiHelpers";
import {
  MAX_LISTINGS,
  MAX_PHOTOS,
  createEmptyListing,
  nextListingId,
} from "./bulkCreate/constants";
import ListingCard from "./bulkCreate/ListingCard";
import PreviewPhotos from "./bulkCreate/PreviewPhotos";
import StepHeader from "./bulkCreate/StepHeader";
import {
  isListingReady,
  listingsToApiRows,
} from "./bulkCreate/validation";

const BulkCreateListings = () => {
  const photosByRowRef = useRef({});
  const [screen, setScreen] = useState("form");
  const [catalogs, setCatalogs] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const [vatPercent, setVatPercent] = useState(15);
  const [listings, setListings] = useState([createEmptyListing()]);
  const [photosByRow, setPhotosByRow] = useState({});
  const [apiRows, setApiRows] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdListings, setCreatedListings] = useState([]);
  const [formError, setFormError] = useState("");

  photosByRowRef.current = photosByRow;

  useEffect(() => {
    let alive = true;
    Promise.all([loadBulkCatalogs(), loadVatPercent()])
      .then(([nextCatalogs, vat]) => {
        if (!alive) return;
        setCatalogs(nextCatalogs);
        setVatPercent(vat);
        setListings((prev) =>
          prev.map((listing) => ({
            ...listing,
            taxes: String(vat),
            cancellationPolicy:
              listing.cancellationPolicy ||
              nextCatalogs.cancellationPolicies?.[0]?.id ||
              "",
            bedTypes:
              listing.bedTypes?.map((t) => t || nextCatalogs.bedTypes?.[0]?.id) ||
              [nextCatalogs.bedTypes?.[0]?.id || ""],
            bathroomTypes:
              listing.bathroomTypes?.map(
                (t) => t || nextCatalogs.bathroomTypes?.[0]?.id,
              ) || [nextCatalogs.bathroomTypes?.[0]?.id || ""],
          })),
        );
      })
      .catch((error) => {
        if (alive) {
          setCatalogError(
            error.response?.data?.message ||
              error.message ||
              "Could not load enabled listing options.",
          );
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    return () => revokePhotoUrls(photosByRowRef.current);
  }, []);

  const readyCount = useMemo(
    () => listings.filter((l) => isListingReady(l)).length,
    [listings],
  );
  const allReady = listings.length > 0 && readyCount === listings.length;

  const updateListing = (id, patch) => {
    setListings((prev) =>
      prev.map((listing) =>
        listing.id === id ? { ...listing, ...patch } : listing,
      ),
    );
  };

  const addListing = () => {
    if (listings.length >= MAX_LISTINGS) {
      toast.error(`You can add at most ${MAX_LISTINGS} listings per batch.`);
      return;
    }
    setListings((prev) => [
      ...prev.map((l) => ({ ...l, open: false })),
      createEmptyListing({
        taxes: String(vatPercent),
        cancellationPolicy: catalogs?.cancellationPolicies?.[0]?.id || "",
        bedTypes: [catalogs?.bedTypes?.[0]?.id || ""],
        bathroomTypes: [catalogs?.bathroomTypes?.[0]?.id || ""],
      }),
    ]);
  };

  const duplicateListing = (id) => {
    if (listings.length >= MAX_LISTINGS) {
      toast.error(`You can add at most ${MAX_LISTINGS} listings per batch.`);
      return;
    }
    setListings((prev) => {
      const source = prev.find((l) => l.id === id);
      if (!source) return prev;
      const copy = {
        ...source,
        id: nextListingId(),
        open: true,
        title: source.title ? `${source.title} (copy)` : "",
        serverErrors: [],
      };
      return [
        ...prev.map((l) => ({ ...l, open: l.id === id ? false : l.open })),
        copy,
      ];
    });
  };

  const removeListing = (id) => {
    setListings((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((l) => l.id !== id);
    });
  };

  const clearServerErrors = () => {
    setListings((prev) =>
      prev.map((listing) => ({ ...listing, serverErrors: [] })),
    );
  };

  const applyServerErrors = (errors) => {
    setListings((prev) =>
      prev.map((listing, index) => {
        const rowNumber = index + 2;
        const serverErrors = (errors || []).filter(
          (err) => err.rowNumber === rowNumber,
        );
        return {
          ...listing,
          open: serverErrors.length ? true : listing.open,
          serverErrors,
        };
      }),
    );
  };

  const handleContinue = async () => {
    if (!allReady || !catalogs) return;
    setFormError("");
    clearServerErrors();
    setIsValidating(true);
    const rows = listingsToApiRows(listings);
    try {
      const response = await validateListingsOnServer(rows);
      if (!response.success) {
        applyServerErrors(response.errors || []);
        toast.error("Fix the highlighted listings before continuing.");
        return;
      }
      setApiRows(rows);
      setScreen("preview");
      toast.success("Listings look valid. Attach photos next.");
    } catch (error) {
      const response = error.response?.data;
      if (Array.isArray(response?.errors)) {
        applyServerErrors(response.errors);
        toast.error("Fix the highlighted listings before continuing.");
      } else {
        setFormError(
          response?.message ||
            error.message ||
            "Could not validate listings. Please try again.",
        );
      }
    } finally {
      setIsValidating(false);
    }
  };

  const replacePhotosByRow = (next) => {
    revokePhotoUrls(photosByRowRef.current);
    setPhotosByRow(next);
  };

  const handleAddPhotos = async (rowNumber, event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    const existing = photosByRow[rowNumber] || [];
    const remaining = MAX_PHOTOS - existing.length;
    if (remaining <= 0) {
      toast.error(`At most ${MAX_PHOTOS} photos per listing.`);
      return;
    }

    const selected = files.slice(0, remaining);
    try {
      await Promise.all(selected.map((file) => validatePhotoFile(file)));
      const entries = selected.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      setPhotosByRow((prev) => ({
        ...prev,
        [rowNumber]: [...(prev[rowNumber] || []), ...entries],
      }));
    } catch (error) {
      toast.error(error.message || "Could not add photos.");
    }
  };

  const removePhoto = (rowNumber, photoId) => {
    setPhotosByRow((prev) => {
      const current = prev[rowNumber] || [];
      const target = current.find((p) => p.id === photoId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return {
        ...prev,
        [rowNumber]: current.filter((p) => p.id !== photoId),
      };
    });
  };

  const setCoverPhoto = (rowNumber, photoId) => {
    setPhotosByRow((prev) => {
      const current = prev[rowNumber] || [];
      const cover = current.find((p) => p.id === photoId);
      if (!cover) return prev;
      return {
        ...prev,
        [rowNumber]: [cover, ...current.filter((p) => p.id !== photoId)],
      };
    });
  };

  const handleCreate = async () => {
    if (!apiRows.length) return;
    setIsSubmitting(true);
    setFormError("");
    try {
      const response = await submitBulkListings(apiRows, photosByRow);
      if (!response.success) {
        if (Array.isArray(response.errors)) {
          applyServerErrors(response.errors);
          setScreen("form");
          toast.error("Create failed. Fix the listed errors and try again.");
        } else {
          setFormError(response.message || "Create failed.");
        }
        return;
      }
      replacePhotosByRow({});
      setCreatedListings(response.listings || []);
      setScreen("done");
      toast.success(
        `Created ${response.createdCount || response.listings?.length || 0} listing(s).`,
      );
    } catch (error) {
      const response = error.response?.data;
      if (Array.isArray(response?.errors)) {
        applyServerErrors(response.errors);
        setScreen("form");
        toast.error("Create failed. Fix the listed errors and try again.");
      } else {
        setFormError(
          response?.message ||
            error.message ||
            "Create failed. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAll = () => {
    replacePhotosByRow({});
    setListings([
      createEmptyListing({
        taxes: String(vatPercent),
        cancellationPolicy: catalogs?.cancellationPolicies?.[0]?.id || "",
        bedTypes: [catalogs?.bedTypes?.[0]?.id || ""],
        bathroomTypes: [catalogs?.bathroomTypes?.[0]?.id || ""],
      }),
    ]);
    setApiRows([]);
    setCreatedListings([]);
    setFormError("");
    setScreen("form");
  };

  return (
    <div className="page-content">
      <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
        <div className="flex flex-col gap-6 p-6">
          <Link
            to="/property"
            className="flex w-fit items-center gap-2 text-secondaryColor"
          >
            <ArrowLeft size={16} />
            Back to listings
          </Link>

          <div>
            <h1 className="text-2xl dark:text-[#c7d8eb]">Bulk listing creator</h1>
            <p className="mt-2 max-w-3xl text-gray-600 dark:text-gray-300">
              Fill one card per property. Repeat the same mobile number on
              multiple cards to assign several listings to one account. After
              preview, attach photos, then create and submit for approval.
            </p>
          </div>

          {screen !== "done" && <StepHeader screen={screen} />}

          {catalogError && (
            <div role="alert" className="rounded-md bg-red-50 p-4 text-red-700">
              Could not load listing options. {catalogError}
            </div>
          )}

          {formError && (
            <div role="alert" className="rounded-md bg-red-50 p-4 text-red-700">
              {formError}
            </div>
          )}

          {screen === "form" && catalogs && (
            <>
              <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                {listings.length} listing
                {listings.length === 1 ? "" : "s"} in this batch
                {" · "}
                {allReady
                  ? "Everything is filled. Continue to the preview."
                  : `${readyCount} ready · ${listings.length - readyCount} still need attention`}
              </div>

              <div className="flex flex-col gap-4">
                {listings.map((listing, index) => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    index={index}
                    catalogs={catalogs}
                    vatPercent={vatPercent}
                    canRemove={listings.length > 1}
                    onChange={(patch) => updateListing(listing.id, patch)}
                    onDuplicate={() => duplicateListing(listing.id)}
                    onRemove={() => removeListing(listing.id)}
                  />
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={addListing}
                  disabled={listings.length >= MAX_LISTINGS}
                  className="flex items-center gap-2 rounded-md border border-secondaryColor px-4 py-2 text-secondaryColor disabled:opacity-50"
                >
                  <Plus size={16} />
                  Add listing
                </button>
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={!allReady || isValidating}
                  className="rounded-md bg-secondaryColor px-4 py-2 text-white disabled:opacity-50"
                >
                  {isValidating
                    ? "Validating..."
                    : "Continue to preview"}
                </button>
              </div>
            </>
          )}

          {screen === "preview" && (
            <PreviewPhotos
              listings={listings}
              apiRows={apiRows}
              catalogs={catalogs || {}}
              photosByRow={photosByRow}
              isSubmitting={isSubmitting}
              onAddPhotos={handleAddPhotos}
              onRemovePhoto={removePhoto}
              onSetCover={setCoverPhoto}
              onCreate={handleCreate}
              onBack={() => setScreen("form")}
            />
          )}

          {screen === "done" && (
            <section className="rounded-md border border-green-300 bg-green-50 p-4 text-green-800">
              <h2 className="font-semibold">
                Created {createdListings.length} listing
                {createdListings.length === 1 ? "" : "s"} and submitted for
                approval.
              </h2>
              <ul className="mt-2 list-inside list-disc">
                {createdListings.map((listing, index) => (
                  <li key={String(listing.propertyId)}>
                    {index + 1}. {listing.title} (host {listing.mobileNumber},
                    property {String(listing.propertyId)}
                    {listing.photoCount
                      ? `, ${listing.photoCount} photos`
                      : ""}
                    )
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-sm">
                They appear under{" "}
                <Link to="/listings-requests" className="underline">
                  Listing Requests
                </Link>{" "}
                pending approval.
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-4 rounded-md bg-secondaryColor px-4 py-2 text-white"
              >
                Create another batch
              </button>
            </section>
          )}
        </div>
      </Container>
    </div>
  );
};

export default BulkCreateListings;
